import type { AccommodationMetadata, BedType } from "~/lib/api";
import { Icon } from "~/components/ui/icons/icon";
import { pluralize } from "~/lib/format";
import { Tag } from "~/components/ui/tag";
import { StatsLine } from "../StatsLine";
import { BED_TYPE_LABELS, formatWorkSummary } from "./accommodationLabels";
import { type Amenity, AMENITIES, AMENITY_LABELS } from "./amenities";

interface AccommodationStatsProps {
  metadata: AccommodationMetadata | null | undefined;
  compact?: boolean;
  outline?: boolean;
  className?: string;
}

export function AccommodationStats({ metadata, compact, className }: AccommodationStatsProps) {
  if (!metadata) return null;

  const view = deriveStatsView(metadata, !!compact);
  if (!view) return null;

  return (
    <div className={className}>
      {view.statsLine && <PropertyStatsLine {...view.statsLine} />}
      {view.tags && <AccommodationTags {...view.tags} />}
    </div>
  );
}

function deriveStatsView(metadata: AccommodationMetadata, compact: boolean) {
  const derived = deriveFields(metadata, compact);
  if (!derived) return null;
  return {
    statsLine: buildStatsLineProps(metadata, derived, compact),
    tags: buildTagsProps(metadata, compact, derived),
  };
}

function deriveFields(metadata: AccommodationMetadata, compact: boolean) {
  const { guests, bedrooms, beds, bathrooms, amenities, workspaces, petsAllowed, childrenAllowed } = metadata;
  const totalBeds = countSleepingBeds(beds);
  const hasStats = !!(guests || bedrooms || totalBeds || bathrooms);
  const hasBikeStorage = !!amenities?.includes("bike-storage");
  const isWorkFriendly = !!(workspaces && workspaces > 0);
  const hasGuestPolicies = !!(petsAllowed || childrenAllowed);
  const displayAmenities = compact ? [] : AMENITIES.filter((a) => amenities?.includes(a)).slice(0, 5);

  const hasAny = hasStats || hasBikeStorage || isWorkFriendly || hasGuestPolicies || displayAmenities.length > 0;
  if (!hasAny) return null;

  return { totalBeds, hasStats, hasBikeStorage, isWorkFriendly, hasGuestPolicies, displayAmenities };
}

function buildStatsLineProps(
  metadata: AccommodationMetadata,
  fields: NonNullable<ReturnType<typeof deriveFields>>,
  compact: boolean,
): PropertyStatsLineProps | null {
  const hasCompactExtras = compact && (fields.hasBikeStorage || fields.isWorkFriendly || fields.hasGuestPolicies);
  if (!fields.hasStats && !hasCompactExtras) return null;

  return {
    guests: metadata.guests,
    bedrooms: metadata.bedrooms,
    totalBeds: fields.totalBeds,
    beds: metadata.beds,
    bathrooms: metadata.bathrooms,
    hasBikeStorage: compact && fields.hasBikeStorage,
    isWorkFriendly: compact && fields.isWorkFriendly,
    petsAllowed: compact && !!metadata.petsAllowed,
    childrenAllowed: compact && !!metadata.childrenAllowed,
  };
}

function buildTagsProps(
  metadata: AccommodationMetadata,
  compact: boolean,
  fields: NonNullable<ReturnType<typeof deriveFields>>,
): AccommodationTagsProps | null {
  if (compact) return null;
  if (!fields.isWorkFriendly && !fields.hasGuestPolicies && fields.displayAmenities.length === 0) return null;

  return {
    workSummary: fields.isWorkFriendly ? formatWorkSummary(metadata) : undefined,
    amenities: fields.displayAmenities,
    petsAllowed: !!metadata.petsAllowed,
    childrenAllowed: !!metadata.childrenAllowed,
  };
}

interface PropertyStatsLineProps {
  guests?: number;
  bedrooms?: number;
  totalBeds: number;
  beds: AccommodationMetadata["beds"];
  bathrooms?: number;
  hasBikeStorage: boolean;
  isWorkFriendly: boolean;
  petsAllowed: boolean;
  childrenAllowed: boolean;
}

function PropertyStatsLine({
  guests,
  bedrooms,
  totalBeds,
  beds,
  bathrooms,
  hasBikeStorage,
  isWorkFriendly,
  petsAllowed,
  childrenAllowed,
}: PropertyStatsLineProps) {
  return (
    <StatsLine>
      <GuestCapacity guests={guests} petsAllowed={petsAllowed} childrenAllowed={childrenAllowed} />
      {!!bedrooms && (
        <span className="inline-flex items-center gap-0.5">
          <Icon i="bedroom" className="size-4" />
          {bedrooms}
        </span>
      )}
      {!!totalBeds && (
        <span
          className="inline-flex items-center gap-0.5"
          title={formatBedBreakdown(beds)}
          aria-label={formatBedBreakdown(beds)}
        >
          <Icon i="bed" className="size-4" />
          {totalBeds}
        </span>
      )}
      {!!bathrooms && (
        <span className="inline-flex items-center gap-0.5">
          <Icon i="bathroom" className="size-4" />
          {bathrooms}
        </span>
      )}
      {hasBikeStorage && (
        <span className="inline-flex items-center gap-0.5 ml-0.5">
          <Icon i="bike-storage" className="size-4" />
          {AMENITY_LABELS["bike-storage"]}
        </span>
      )}
      {isWorkFriendly && (
        <span className="inline-flex items-center gap-0.5 ml-0.5">
          <Icon i="desk" className="size-4" />
          Work-friendly
        </span>
      )}
    </StatsLine>
  );
}

function GuestCapacity({
  guests,
  petsAllowed,
  childrenAllowed,
}: {
  guests?: number;
  petsAllowed: boolean;
  childrenAllowed: boolean;
}) {
  const hasPolicies = petsAllowed || childrenAllowed;
  if (!guests && !hasPolicies) return null;

  return (
    <span className="inline-flex items-center gap-1">
      {!!guests && pluralize(guests, "guest")}
      {hasPolicies && <GuestPolicyIcons pets={petsAllowed} kids={childrenAllowed} />}
    </span>
  );
}

function GuestPolicyIcons({ pets, kids }: { pets: boolean; kids: boolean }) {
  return (
    <span className="inline-flex items-center gap-0.5" aria-label="Guest policies">
      <span className="text-xs">{"("}</span>
      <span className="inline-flex items-center gap-1">
        {pets && (
          <span aria-label="Pet-friendly">
            <Icon i="pet-friendly" className="size-3.5" />
          </span>
        )}
        {kids && (
          <span aria-label="Child-friendly">
            <Icon i="child-friendly" className="size-3.5" />
          </span>
        )}
      </span>
      <span className="text-xs">{")"}</span>
    </span>
  );
}

interface AccommodationTagsProps {
  workSummary?: string;
  amenities: Amenity[];
  petsAllowed: boolean;
  childrenAllowed: boolean;
}

function AccommodationTags({ workSummary, amenities, petsAllowed, childrenAllowed }: AccommodationTagsProps) {
  return (
    <div className="flex flex-wrap gap-1.5 mt-1">
      {workSummary && (
        <Tag aria-label="Work-friendly">
          <Icon i="desk" className="size-3.5" />
          {workSummary}
        </Tag>
      )}
      {amenities.map((amenity) => (
        <Tag key={amenity} aria-label={AMENITY_LABELS[amenity]}>
          <Icon i={amenity} className="size-3.5" />
          {AMENITY_LABELS[amenity]}
        </Tag>
      ))}
      {petsAllowed && (
        <Tag aria-label="Pet-friendly">
          <Icon i="pet-friendly" className="size-3.5" />
          Pet-friendly
        </Tag>
      )}
      {childrenAllowed && (
        <Tag aria-label="Child-friendly">
          <Icon i="child-friendly" className="size-3.5" />
          Child-friendly
        </Tag>
      )}
    </div>
  );
}

function formatBedBreakdown(beds: AccommodationMetadata["beds"]): string | undefined {
  if (!beds) return undefined;
  const { bassinet, ...sleeping } = beds;
  const entries = (Object.entries(sleeping) as [BedType, number | undefined][]).filter(
    (entry): entry is [BedType, number] => !!entry[1] && entry[1] > 0,
  );
  if (entries.length === 0) return undefined;
  return entries.map(([type, count]) => `${count} ${BED_TYPE_LABELS[type]}`).join(", ");
}

function countSleepingBeds(beds: AccommodationMetadata["beds"]): number {
  if (!beds) return 0;
  const { bassinet, ...sleeping } = beds;
  return Object.values(sleeping).reduce((sum, n) => sum + (n ?? 0), 0);
}
